import { useTellescopeMutation, useTellescopeQuery } from "@tellescope/tanstack-query";
import { formatDateTime } from "@tellescope/utils";
import { FormAnswer } from "../../ContactsDialog/sections/Forms/FormAnswer";
import { Button, DestructiveConfirmDialog, useUncontrolledState } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { ListChecks, Pencil, Trash2 } from "lucide-react";
import { Dispatch, SetStateAction } from "react";
import { SinglePageFormSubmission } from "../../ContactsDialog/sections/Forms/SinglePageFormSubmission";

export interface EditableFormContextCardProps {
  formResponseId: string;
  isEditing?: boolean;
  setIsEditing?: Dispatch<SetStateAction<boolean>>;
  onSubmit?: () => void;
}

export function EditableFormContextCard({
  formResponseId,
  isEditing: providedIsEditing,
  setIsEditing: providedSetIsEditing,
  onSubmit,
}: EditableFormContextCardProps) {
  const { t } = useTranslation("forms");
  const [isEditing, setIsEditing] = useUncontrolledState({
    state: providedIsEditing ?? false,
    setState: providedSetIsEditing,
  });

  // Get the form response
  const formResponseQuery = useTellescopeQuery({
    model: "form_responses",
    method: "getOne",
    request: formResponseId,
    enabled: !!formResponseId,
  });

  const closeFormSubmission = () => {
    setIsEditing(false);
  };

  const deleteMutation = useTellescopeMutation({
    model: "form_responses",
    method: "deleteOne",
  });

  const response = formResponseQuery.data;

  if (formResponseQuery.isLoading) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <p className="text-sm">{t("Loading form response...")}</p>
      </div>
    );
  }

  if (formResponseQuery.isError || !response) {
    return (
      <div className="p-4 border border-destructive rounded-lg">
        <p className="text-sm text-destructive">{t("Unable to load form response. Please try again.")}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 h-full w-full border border-gray-200 rounded-lg bg-white overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 bg-gray-200">
        <div className="flex gap-2 items-center">
          <ListChecks className="h-4 w-4" />
          <p className="font-medium text-sm">
            {response.formTitle.toLowerCase().trim().endsWith("form")
              ? response.formTitle
              : t("{{formTitle}} Form", { formTitle: response.formTitle })}
          </p>
          <p className="text-sm text-muted-foreground">{formatDateTime(response.createdAt)}</p>
        </div>
        <div className="flex items-center">
          <DestructiveConfirmDialog onConfirm={() => deleteMutation.mutateAsync(response.id)}>
            <Button size="icon" onClick={() => {}} variant="ghost" hoverLabel={t("Delete Response")}>
              <Trash2 />
            </Button>
          </DestructiveConfirmDialog>
          <Button size="icon" onClick={() => setIsEditing(true)} variant="ghost" hoverLabel={t("Edit Response")}>
            <Pencil />
          </Button>
        </div>
      </div>

      <div className="flex flex-col gap-3 overflow-y-auto p-4 w-full">
        {isEditing ? (
          <SinglePageFormSubmission
            onSubmit={() => {
              onSubmit?.();
              closeFormSubmission();
            }}
            onSaveResponse={closeFormSubmission}
            onCancel={closeFormSubmission}
            contactId={response.enduserId}
            responseId={response.id}
            parentResponseId={response.id}
            rootResponseId={response.rootResponseId}
            formId={response.formId}
          />
        ) : response.responses?.length ? (
          response.responses.map((item) => (
            <FormAnswer title={item.fieldTitle} answer={item.answer} key={item.fieldId} />
          ))
        ) : (
          <div className="text-center py-8 text-muted-foreground">{t("No response data available")}</div>
        )}
      </div>
    </div>
  );
}
